import type { Pt2, Segment } from "./parse";

/**
 * Doğru parçalarından kapalı bölgeler: uçlar toleransla düğümlere yapıştırılır, sarkan (tek uçlu) kenarlar budanır,
 * düzlemsel yüz izleme (her düğümde gidiş yönüne göre en soldaki kenar) ile yüzler çıkarılır; dış yüz (saat yönü) atılır.
 * Kesişen ama düğümü olmayan çizgiler bölünmez (kullanıcı çizimde köşeleri gerçek uçlarla çizmiş olmalı).
 */
export interface Face { points: Pt2[]; area: number; score: number }

const signedArea = (p: Pt2[]): number => { let a = 0; for (let i = 0; i < p.length; i++) { const q = p[i]!, r = p[(i + 1) % p.length]!; a += q.x * r.y - r.x * q.y; } return a / 2; };
export const extentOf = (pts: Pt2[]): number => { if (!pts.length) return 0; let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity; for (const p of pts) { x0 = Math.min(x0, p.x); x1 = Math.max(x1, p.x); y0 = Math.min(y0, p.y); y1 = Math.max(y1, p.y); } return Math.max(x1 - x0, y1 - y0); };

export function findFaces(segments: Segment[], extraLoops: Pt2[][] = []): Face[] {
  const allPts = segments.flatMap((s) => [s.a, s.b]);
  const tol = Math.max(extentOf(allPts) * 1e-5, 1e-9);
  // Düğümler (ızgara sözlüğüyle yaklaşık eşleme)
  const nodes: Pt2[] = [];
  const grid = new Map<string, number[]>();
  const key = (p: Pt2): string => `${Math.round(p.x / tol / 4)}:${Math.round(p.y / tol / 4)}`;
  const nodeOf = (p: Pt2): number => {
    const kx = Math.round(p.x / tol / 4), ky = Math.round(p.y / tol / 4);
    for (let dx = -1; dx <= 1; dx++) for (let dy = -1; dy <= 1; dy++) for (const i of grid.get(`${kx + dx}:${ky + dy}`) ?? []) if (Math.hypot(nodes[i]!.x - p.x, nodes[i]!.y - p.y) <= tol) return i;
    nodes.push({ x: p.x, y: p.y }); const i = nodes.length - 1; const k = key(p); grid.set(k, [...(grid.get(k) ?? []), i]); return i;
  };
  const adj = new Map<number, Set<number>>();
  const link = (u: number, v: number): void => { if (!adj.has(u)) adj.set(u, new Set()); if (!adj.has(v)) adj.set(v, new Set()); adj.get(u)!.add(v); adj.get(v)!.add(u); };
  for (const s of segments) { const u = nodeOf(s.a), v = nodeOf(s.b); if (u !== v) link(u, v); }
  // Sarkan kenarları buda
  let changed = true;
  while (changed) { changed = false; for (const [u, nb] of adj) if (nb.size === 1) { const v = [...nb][0]!; adj.get(v)!.delete(u); adj.delete(u); changed = true; } }
  // Yüz izleme
  const ang = (u: number, v: number): number => Math.atan2(nodes[v]!.y - nodes[u]!.y, nodes[v]!.x - nodes[u]!.x);
  const used = new Set<string>();
  const faces: Face[] = [];
  let maxLen = 2; for (const nb of adj.values()) maxLen += nb.size;                 // bir yüz yönlü kenar sayısından uzun olamaz
  for (const [u0, nb0] of adj) for (const v0 of nb0) {
    if (used.has(`${u0}>${v0}`)) continue;
    const ring: number[] = [u0];
    let u = u0, v = v0, guard = 0;
    while (guard++ < maxLen) {
      used.add(`${u}>${v}`); ring.push(v);
      const t = ang(u, v);
      // Sol yüz izleme: gidiş yönüne göre en soldaki (en büyük sola dönüş, düz gidiş 0, sağa dönüş negatif); geri dönüş yalnız çıkmazda
      let best: number | null = null, bestTurn = -Infinity;
      for (const w of adj.get(v)!) { if (w === u) continue; let turn = ang(v, w) - t; while (turn <= -Math.PI) turn += 2 * Math.PI; while (turn > Math.PI) turn -= 2 * Math.PI; if (turn > bestTurn) { bestTurn = turn; best = w; } }
      if (best === null) best = u;
      u = v; v = best;
      if (u === u0 && v === v0) break;
    }
    ring.pop();
    const pts = ring.map((i) => nodes[i]!);
    const a = signedArea(pts);
    if (a > tol * tol && pts.length >= 3) faces.push({ points: pts, area: a, score: 0 });
  }
  // Kapalı polyline'lar yüz olarak bulunmadıysa (içinden başka çizgi geçiyor) doğrudan aday
  for (const loop of extraLoops) {
    const a = signedArea(loop); if (Math.abs(a) <= tol * tol) continue;
    const pts = a < 0 ? [...loop].reverse() : loop;
    const c = centroid(pts);
    if (!faces.some((f) => Math.abs(f.area - Math.abs(a)) < Math.abs(a) * 1e-6 && Math.hypot(centroid(f.points).x - c.x, centroid(f.points).y - c.y) < tol * 10)) faces.push({ points: pts, area: Math.abs(a), score: 0 });
  }
  for (const f of faces) f.score = wallScore(f, tol);
  return faces.sort((p, q) => q.score - p.score || q.area - p.area);
}

function centroid(p: Pt2[]): Pt2 { let x = 0, y = 0; for (const q of p) { x += q.x; y += q.y; } return { x: x / p.length, y: y / p.length }; }

/** Duvar kesitine benzerlik: taban yatay (+2), altta üstten geniş (+1), H/B 0,3–8 (+1). */
function wallScore(f: Face, tol: number): number {
  const ys = f.points.map((p) => p.y), xs = f.points.map((p) => p.x);
  const y0 = Math.min(...ys), y1 = Math.max(...ys);
  const base = f.points.filter((p) => Math.abs(p.y - y0) < tol * 10), top = f.points.filter((p) => Math.abs(p.y - y1) < tol * 10);
  let s = 0;
  if (base.length >= 2) s += 2;
  const bw = base.length ? Math.max(...base.map((p) => p.x)) - Math.min(...base.map((p) => p.x)) : 0;
  const tw = top.length ? Math.max(...top.map((p) => p.x)) - Math.min(...top.map((p) => p.x)) : 0;
  if (bw >= tw) s += 1;
  const H = y1 - y0, W = Math.max(...xs) - Math.min(...xs);
  if (W > 0 && H / W >= 0.3 && H / W <= 8) s += 1;
  return s;
}

/** Seçilen (bitişik) yüzlerin birleşimi: ortak kenarlar (karşıt yönlü çiftler) atılır, kalanlar tek halkaya zincirlenir. */
export function unionFaces(faces: Face[]): { points: Pt2[] } | { error: string } {
  if (!faces.length) return { error: "Bölge seçilmedi" };
  if (faces.length === 1) return { points: faces[0]!.points };
  const tol = Math.max(extentOf(faces.flatMap((f) => f.points)) * 1e-5, 1e-9);
  const same = (p: Pt2, q: Pt2): boolean => Math.hypot(p.x - q.x, p.y - q.y) <= tol;
  type E = { a: Pt2; b: Pt2 };
  const edges: E[] = [];
  for (const f of faces) for (let i = 0; i < f.points.length; i++) edges.push({ a: f.points[i]!, b: f.points[(i + 1) % f.points.length]! });
  const keep: E[] = [];
  const dropped = new Set<number>();
  for (let i = 0; i < edges.length; i++) {
    if (dropped.has(i)) continue;
    const j = edges.findIndex((e, k) => k > i && !dropped.has(k) && same(e.a, edges[i]!.b) && same(e.b, edges[i]!.a));
    if (j >= 0) { dropped.add(i); dropped.add(j); } else keep.push(edges[i]!);
  }
  if (keep.length === edges.length) return { error: "Seçilen bölgeler bitişik değil (ortak kenar yok)" };
  const ring: Pt2[] = [keep[0]!.a];
  let cur = keep[0]!.b; const usedE = new Set<number>([0]);
  for (let guard = 0; guard < keep.length; guard++) {
    if (same(cur, ring[0]!)) break;
    ring.push(cur);
    const k = keep.findIndex((e, i) => !usedE.has(i) && same(e.a, cur));
    if (k < 0) return { error: "Birleşim tek halka vermiyor" };
    usedE.add(k); cur = keep[k]!.b;
  }
  if (usedE.size !== keep.length) return { error: "Seçilen bölgeler tek parça değil (delik ya da ayrık parça)" };
  return { points: ring };
}
